import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import mapboxgl from 'mapbox-gl';

import { LayerContext } from 'state/LayerContext';
import { useGetLayerQuery } from 'state/apiSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectComparison,
  selectCurrentProjectInfo,
  selectLegendSettings,
  selectMapCenter,
  selectMapStyle,
  selectMapZoom,
  selectProject,
  selectRoadFilters,
  selectRouteSelection,
  selectSegmentId,
  selectShortestAvailableBin,
  selectShowBarbs,
  selectShowIncidents,
  selectTargetDate,
  selectTimeOfDay,
  selectTimelineMetric,
  setMapCenter,
  setMapZoom,
  setRouteSelection,
  setSelectedSegmentId,
  setShowIncidents,
} from 'state/workflowSlice';

import { optionallyCall } from 'appUtils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { formatTimeOfDay, nowUnix } from 'features/common/datetimeUtils';
import { useBarbs } from 'features/map/hooks/useBarbs';
import { useMapResize } from 'features/map/hooks/useMapResize';
import { useProjectBoundary } from 'features/map/hooks/useProjectBoundary';
import { useRoadClassFilterTooltip } from 'features/map/hooks/useRoadClassFilterTooltip';
import ScreenshotExtras from 'features/map/overlays/ScreenshotExtras';
import { RoadClassTooltip } from 'features/map/tooltips/RoadClassTooltip/RoadClassTooltip';
import { useZoomPrompt } from 'features/map/useZoomPrompt';
import {
  addOrUpdateMapHoveredSegment,
  getInitialCenter,
  getNearestSegmentFromMouseEvent,
  getRoadClassLayerFilter,
  initMap,
  mapHasLayer,
  mapPopSelectedSegment,
  mapShowingSegments,
  mapShowingSelectedSegment,
  mergeMapLayerFilters,
  redrawBaseSegments,
  safeRemoveBookmarks,
  setHoverCursorTypeForHover,
  showBookmarks,
  updateMapLocation,
} from 'features/map/utils/mapCommon';
import { MapControl, MapControlValue } from 'features/map/utils/mapControls';
import {
  getHoveredSegmentLayerArrowDefinition,
  getHoveredSegmentLayerDefinition,
  kSegmentLayerArrowDefinition,
} from 'features/map/utils/mapLayerDefinitions';
import { ensureRtlLoaded, visibleProperty } from 'features/map/utils/mapUtils';
import {
  SPEEDS_NULL_PLACEHOLDER_THRESHOLD,
  counts_absolute_size_expr,
  getTLMetricOptions,
  isRealtimeMetric,
  metricExprDeepSub,
} from 'features/workflow_timeline/metricsOptions';
import { semi_transparent_null_grey } from 'theme/cemTheme';

import { useCallbackWithErrorHandling } from '../../app/ErrorHandling';
import { TIMELINE_MIN_ZOOM, mapboxApiKey } from '../../appConstants';
import { Segment } from '../../appTypes';
import {
  selectDisplayedBookmark,
  selectInLocationEditMode,
  setDisplayedBookmark,
} from '../../state/bookmarkSlices';
import {
  selectVnrtData,
  setRepresentedSlots,
} from '../../state/realtimeSlices';
import { ProjectFeatures } from '../../state/userSlice';
import { minutes_to_slots } from '../common/slotsUtils';
import { MapContext } from '../map/MapContext';
import { MapLoadingSpinner } from '../map/MapLoadingSpinner';
import { MapSettings } from '../map/overlays/MapSettings';
import { OutsideBboxPrompt } from '../map/overlays/OutsideBboxPrompt';
import { latLngToJsonPoint, launderLatLng } from '../map/utils/geography_utils';
import {
  findNearestIncidentInBox,
  kIncidentsShowHideDisabledMessage,
  kIncidentsShowHideNormalMessage,
  redrawIncidents,
  setIncidentLayerVisibility,
  shouldShowIncidents,
} from '../map/utils/incidentsLayer';
import {
  MIN_MAP_ZOOM,
  icon_size_expression,
  kHalfDirectionArrowNameLHD,
  kHalfDirectionArrowNameRHD,
  kMainVisLineOpacity,
  kMainVisLineOpacityExpression,
  kMouseHoverTimeoutMillis,
  kSegmentHoverId,
  kSegmentLayerId,
  kSegmentLayerSourceLayerId,
  kSegmentSourceId,
  kSegmentSymbolsLayerId,
  kTicksShowHideId,
  thick_line_width_expression,
} from '../map/utils/map_constants';
import { FloatingDiv } from '../spinner/Spinner';
import { useDayInfo } from '../task_bar/DatePicker';
import { getMinuteTicker, useRealtimeData } from './RealtimeDataProvider';
import { TimelineLegend } from './TimelineLegend';
import {
  TimelineMapSegmentTooltip,
  TimelineTooltipData,
} from './TimelineMapSegmentTooltip';

// mapbogxl style should be imported BEFORE Map.css as it must override a handful of styles
import 'mapbox-gl/dist/mapbox-gl.css';

import 'features/map/Map.css';

import styles from '../map/Map.module.css';

dayjs.extend(utc);
mapboxgl.accessToken = mapboxApiKey;

const kColorUpdateMillis = 200;
const kIncidentsShowHideId = 'incidents-showhide';
const kUpdateSlotsRepresentedTimeoutMs = 1000;

let lastColorUpdate;
let lastColorUpdateRef;

export function TimelineMap() {
  // https://docs.mapbox.com/help/tutorials/use-mapbox-gl-js-with-react/

  const dispatch = useAppDispatch();

  // useRef DOM refs and for variables accessed from map callbacks
  const { refMap, mapReady, setMapReady, refStyleReady } =
    useContext(MapContext);
  const refMapContainer = useRef(null); // DOM node reference
  const refRouting = useRef({});
  const refMouseoverTimeout = useRef(null);
  const hourlyMaxCount = useRef(1000);

  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const {
    roadFilterOpen,
    setRoadFilterOpen,
    anchorEl,
    handleRoadClassFilterClickCallback,
  } = useRoadClassFilterTooltip();
  const [tooltipData, setTooltipData] = useState<
    undefined | TimelineTooltipData
  >(undefined);
  const refCurrentTooltipSegment = useRef<Segment | undefined>(
    tooltipData?.segment,
  );

  const { layer } = useContext(LayerContext);
  const selectedSegmentId = useAppSelector(selectSegmentId);
  const selectedRoute = useAppSelector(selectRouteSelection);
  const project = useAppSelector(selectProject);
  const currentMapCenter = useAppSelector(selectMapCenter);
  const mapZoom = useAppSelector(selectMapZoom);
  const targetDate = useAppSelector(selectTargetDate);
  const timeOfDay = useAppSelector(selectTimeOfDay);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const binSize = useAppSelector(selectShortestAvailableBin);
  const timelineMetric = useAppSelector(selectTimelineMetric);
  const refTimelineMetric = useRef(timelineMetric);
  const comparison = useAppSelector(selectComparison);
  const inLocationEditMode = useAppSelector(selectInLocationEditMode);
  const displayedBookmark = useAppSelector(selectDisplayedBookmark);
  const showBarbs = useAppSelector(selectShowBarbs);
  const vnrtData = useAppSelector(selectVnrtData);
  const refVnrtData = useRef(vnrtData);

  const refEnrichedTiles = useRef({});
  const refUpdateSlotsRepresentedTimeout = useRef(null);
  const refTargetDate = useRef(targetDate);
  const refProjectUsesMetric = useRef(userProject?.uses_metric);
  const mapStyle = useAppSelector(selectMapStyle);
  const showIncidents = useAppSelector(selectShowIncidents);
  const refShowIncidents = useRef(showIncidents);
  const legendSettings = useAppSelector(selectLegendSettings);
  const roadFilters = useAppSelector(selectRoadFilters);

  const zoomPrompt = useZoomPrompt(refMap.current, TIMELINE_MIN_ZOOM);
  const handleTicksShowHideClickCallback = useBarbs(kSegmentSymbolsLayerId);

  useMapResize(refMap.current, refMapContainer);
  const timeOfDayOffset = minutes_to_slots(timeOfDay, binSize || 15);
  const timeOfDayOffsetRef = useRef({
    offset: timeOfDayOffset,
    time: timeOfDay,
  });
  timeOfDayOffsetRef.current = { offset: timeOfDayOffset, time: timeOfDay };
  const refInLocationEditMode = useRef(inLocationEditMode);

  useProjectBoundary(userProject.project_boundary);
  // load segmentData for layer from REST api (or cache)
  const { currentData: layerData } = useGetLayerQuery(layer, { skip: !layer });
  const { day_info } = useDayInfo();
  const refDayInfo = useRef(day_info);
  const t = getMinuteTicker();
  const { realtime: realtimeData } = useRealtimeData(t);
  const refRealtimeData = useRef(realtimeData);

  // useSegmentSelectionCrosswalk();

  refRouting.current = useMemo(() => layerData?.routing, [layerData]);

  const projectHasIncidents = userProject.features.includes(
    ProjectFeatures.INCIDENTS,
  );

  ensureRtlLoaded();

  function getColorExpression(metric) {
    const setting = getTLMetricOptions(mapStyle, metric);
    if (!setting) {
      return undefined;
    }
    const settingsArgs = {
      timeOfDayOffset,
      hourlyMaxCount: Number(hourlyMaxCount.current),
      uses_metric: refProjectUsesMetric.current,
    };
    const selectedType = setting.color_schemes[legendSettings.type];
    const selectedColors = selectedType[legendSettings.scheme];
    const metric_expr = setting.metric_expr(settingsArgs);

    // Discrete mode
    if (legendSettings.type === 'discrete') {
      return [
        'step',
        ['coalesce', metric_expr, SPEEDS_NULL_PLACEHOLDER_THRESHOLD],
        selectedColors[1], // for values below 0
        ...selectedColors,
        SPEEDS_NULL_PLACEHOLDER_THRESHOLD,
        semi_transparent_null_grey,
      ];
    }

    // Continuous mode
    return [
      'interpolate',
      ['linear'],
      ['coalesce', metric_expr, SPEEDS_NULL_PLACEHOLDER_THRESHOLD],
      ...optionallyCall(selectedColors, settingsArgs),
      SPEEDS_NULL_PLACEHOLDER_THRESHOLD,
      semi_transparent_null_grey,
    ];
  }

  function getShowExpression(metric) {
    // We use the shown expression in metrics options to hide typical segments
    // when they wouldn't be rendered anyway due to being transparent.
    // This is because mapboxgl does not do additive mixing within a single layer,
    // Instead writing to the layer gl buffer in one go, in the (randomish) order segments
    // are returned. This means that transparent segments get drawn over other non-transparent
    // segments, which looks very odd. At least when they're visible we can see them and know
    // why occlusion is happening.

    const setting = getTLMetricOptions(mapStyle, metric);
    if (!setting) {
      return undefined;
    }

    const selectedType = setting.color_schemes[legendSettings.type];
    const selectedColors = selectedType[legendSettings.scheme];

    const settingsArgs = {
      timeOfDayOffset,
      hourlyMaxCount: Number(hourlyMaxCount.current),
      uses_metric: refProjectUsesMetric.current,
    };
    if (!setting.shown || selectedColors !== 'neutral') {
      return true;
    } else {
      const shown = metricExprDeepSub(
        [...setting.shown],
        setting.metric_expr(settingsArgs),
      );
      return shown;
    }
  }

  function getLineWidthExpression(metric) {
    const setting = getTLMetricOptions(mapStyle, metric);
    if (setting?.width === 'count_absolute') {
      const counts_expr = (base) =>
        counts_absolute_size_expr(
          timeOfDayOffset,
          Number(hourlyMaxCount.current),
          base,
        );
      return [
        'interpolate',
        ['linear'],
        ['zoom'],
        MIN_MAP_ZOOM,
        counts_expr(2),
        14,
        counts_expr(3),
        16,
        counts_expr(8),
        19,
        counts_expr(20),
      ];
    } else {
      return thick_line_width_expression;
    }
  }

  function getOffsetExpression(forIcon = false): mapboxgl.Expression {
    let sign = -1;
    if (userProject?.right_hand_drive) {
      sign = 1;
    }
    const makeItem = (multiplier) => {
      if (forIcon) {
        return ['literal', [sign * multiplier, sign * multiplier]];
      } else {
        return sign * multiplier;
      }
    };
    return [
      'interpolate',
      ['linear'],
      ['zoom'],
      MIN_MAP_ZOOM,
      makeItem(2),
      14,
      makeItem(2),
      16,
      makeItem(3),
      19,
      makeItem(5),
    ];
  }

  function getIconSizeExpression(metric) {
    const setting = getTLMetricOptions(mapStyle, metric);
    if (setting?.width === 'count_absolute') {
      const counts_expr = (base) =>
        counts_absolute_size_expr(
          timeOfDayOffset,
          Number(hourlyMaxCount.current),
          base,
        );
      return [
        'interpolate',
        ['linear'],
        ['zoom'],
        MIN_MAP_ZOOM,
        counts_expr(0.4),
        14,
        counts_expr(0.4),
        16,
        counts_expr(0.8),
        19,
        counts_expr(1),
      ];
    } else {
      return icon_size_expression;
    }
  }

  const segmentLineAndSymbolFilter = mergeMapLayerFilters(
    getShowExpression(timelineMetric),
    getRoadClassLayerFilter(roadFilters),
  );

  const segmentLayerDefinition = {
    id: kSegmentLayerId,
    type: 'line',
    source: kSegmentSourceId,
    'source-layer': kSegmentLayerSourceLayerId,
    layout: {},
    paint: {
      'line-color': getColorExpression(timelineMetric),
      'line-width': getLineWidthExpression(timelineMetric),
      'line-opacity': 0.7,
      'line-offset': getOffsetExpression(),
    },
    ...(segmentLineAndSymbolFilter
      ? { filter: segmentLineAndSymbolFilter }
      : {}),
  };

  const segmentArrowLayerDefinition = {
    ...kSegmentLayerArrowDefinition,
    ...(segmentLineAndSymbolFilter
      ? { filter: segmentLineAndSymbolFilter }
      : {}),
    paint: {
      'icon-color': getColorExpression(timelineMetric),
      'icon-opacity': kMainVisLineOpacityExpression(kMainVisLineOpacity),
    },
  };

  segmentArrowLayerDefinition.layout['icon-image'] =
    userProject?.right_hand_drive
      ? kHalfDirectionArrowNameRHD
      : kHalfDirectionArrowNameLHD;
  segmentArrowLayerDefinition.layout['icon-size'] = getIconSizeExpression(
    timelineMetric,
  ) as any;
  (segmentArrowLayerDefinition.layout as any).visibility =
    visibleProperty(showBarbs);

  const hoverLayerDefinition = {
    ...segmentLayerDefinition,
    id: kSegmentHoverId,
    paint: {
      ...segmentLayerDefinition.paint,
      'line-color': '#000000',
      'line-opacity': 0,
    },
  };

  const hoveredSegmentLayerDefintion: mapboxgl.LineLayer = {
    ...getHoveredSegmentLayerDefinition(mapStyle),
    paint: {
      ...getHoveredSegmentLayerDefinition(mapStyle).paint,
      'line-offset': getOffsetExpression(),
    },
  };

  const hoveredSegmentArrowLayerDefintion: mapboxgl.SymbolLayer = {
    ...getHoveredSegmentLayerArrowDefinition(mapStyle),
    layout: {
      ...getHoveredSegmentLayerArrowDefinition(mapStyle).layout,
      'icon-offset': getOffsetExpression(true),
    },
  };

  function updateColorExpression() {
    lastColorUpdate = nowUnix();
    const filters = mergeMapLayerFilters(
      getShowExpression(timelineMetric),
      getRoadClassLayerFilter(roadFilters),
    );
    if (filters) {
      refMap.current.setFilter(kSegmentLayerId, filters);
      if (mapHasLayer(refMap.current, kSegmentSymbolsLayerId))
        refMap.current.setFilter(kSegmentSymbolsLayerId, filters);
      if (mapHasLayer(refMap.current, kSegmentHoverId))
        refMap.current.setFilter(kSegmentHoverId, filters);
    }
    refMap.current.setPaintProperty(
      kSegmentLayerId,
      'line-width',
      getLineWidthExpression(timelineMetric),
    );
    refMap.current.setPaintProperty(
      kSegmentLayerId,
      'line-color',
      getColorExpression(timelineMetric),
    );
    if (MapControl.getValue(kTicksShowHideId)) {
      refMap.current.setPaintProperty(
        kSegmentSymbolsLayerId,
        'icon-color',
        getColorExpression(timelineMetric),
      );
      refMap.current.setLayoutProperty(
        kSegmentSymbolsLayerId,
        'icon-size',
        getIconSizeExpression(timelineMetric),
      );
    }
  }

  const handleSegmentClickCallback = useCallbackWithErrorHandling((event) => {
    // console.log(`handleSegmentClickCallback ${JSON.stringify(event.lngLat)} refSegments.current ${JSON.stringify(refSegments.current.length)}`);
    if (refInLocationEditMode.current) {
      dispatch(setDisplayedBookmark(launderLatLng(event.lngLat)));
    } else {
      if (mapShowingSelectedSegment(refMap.current))
        mapPopSelectedSegment(refMap.current);
      dispatch(setRouteSelection(undefined));
      dispatch(setSelectedSegmentId(undefined));
    }
  });

  function showMouseTooltipForFeature(feature, incident) {
    if (refMouseoverTimeout.current) {
      clearTimeout(refMouseoverTimeout.current.handle);
      refMouseoverTimeout.current = undefined;
    }

    // Populate the popup and set its coordinates (otherwise it doesn't show)
    setTooltipData({ segment: feature, incident });

    if (
      feature?.properties?.segment_id !==
      refCurrentTooltipSegment.current?.properties?.segment_id
    ) {
      addOrUpdateMapHoveredSegment(
        refMap.current,
        mapStyle,
        feature,
        hoveredSegmentLayerDefintion,
        hoveredSegmentArrowLayerDefintion,
      );
    }
    refCurrentTooltipSegment.current = feature;
  }

  function hideMouseover() {
    setTooltipData(undefined);
    refMouseoverTimeout.current = undefined;
    refCurrentTooltipSegment.current = undefined;
    addOrUpdateMapHoveredSegment(
      refMap.current,
      mapStyle,
      undefined,
      hoveredSegmentLayerDefintion,
      hoveredSegmentArrowLayerDefintion,
    );
  }

  const handleMouseLeaveEvent = useCallbackWithErrorHandling((event) => {
    if (!refMouseoverTimeout.current) {
      refMouseoverTimeout.current = {
        handle: setTimeout(hideMouseover, kMouseHoverTimeoutMillis),
        set: nowUnix(),
      };
    } else if (
      nowUnix() - refMouseoverTimeout.current.set >
      kMouseHoverTimeoutMillis * 2
    ) {
      hideMouseover();
    }
  });

  const handleMouseMoveEvent = useCallbackWithErrorHandling(
    (event: mapboxgl.MapMouseEvent) => {
      const { point } = event;
      if (!mapShowingSegments(refMap.current)) return;
      const nearestSegment = getNearestSegmentFromMouseEvent(
        refMap.current,
        event,
        kSegmentHoverId,
      );
      const incident = findNearestIncidentInBox(refMap.current, point);
      const hasHover = !!(nearestSegment || incident);
      if (hasHover) {
        showMouseTooltipForFeature(nearestSegment, incident);
      } else {
        handleMouseLeaveEvent(event);
      }
      setHoverCursorTypeForHover(refMap.current, hasHover);
    },
  );

  const handleMapMoveCallback = useCallbackWithErrorHandling(() => {
    const center = {
      lng: refMap.current.getCenter().lng.toFixed(4),
      lat: refMap.current.getCenter().lat.toFixed(4),
    };
    const newZoom = refMap.current.getZoom().toFixed(2);
    dispatch(setMapZoom(newZoom));
    dispatch(setMapCenter(center));
  });

  function handleIncidentsShowHideClickCallback() {
    if (isRealtimeMetric(refTimelineMetric.current)) {
      const newShow = !refShowIncidents.current;
      dispatch(setShowIncidents(newShow));
      MapControl.setValue(newShow, kIncidentsShowHideId);
    }
  }

  function updateFeaturesWithRt() {
    if (refMap.current && mapHasLayer(refMap.current, kSegmentLayerId)) {
      if (refRealtimeData.current) {
        // console.debug('Loading rt data into the map');
        const speed_key = 'rtspeed';
        const rt_keys = Object.keys(refRealtimeData.current.data);
        for (let i = 0; i < rt_keys.length; i++) {
          const id = rt_keys[i];
          refMap.current.setFeatureState(
            {
              source: kSegmentSourceId,
              sourceLayer: kSegmentLayerSourceLayerId,
              id,
            },
            { [speed_key]: refRealtimeData.current.data[id] },
          );
        }
        return true;
      }
    }
    return false;
  }

  function updateFeaturesWithVNRT() {
    if (refMap.current && mapHasLayer(refMap.current, kSegmentLayerId)) {
      const l_vnrtData = refVnrtData.current;
      if (Object.keys(l_vnrtData).length > 0) {
        // console.debug('Loading vnrt data into the map');
        const segs = {};
        const slots_keys = Object.keys(l_vnrtData);
        for (let i = 0; i < slots_keys.length; i++) {
          for (let j = 0; j < l_vnrtData[slots_keys[i]].length; j++) {
            const r = l_vnrtData[slots_keys[i]][j];
            if (!segs[r.s]) segs[r.s] = [];
            segs[r.s].push(r);
          }
        }
        // Segs contains a map of time and value objects
        const segs_keys = Object.keys(segs);
        for (let i = 0; i < segs_keys.length; i++) {
          const id = segs_keys[i];
          const new_state = {};
          for (let j = 0; j < segs[id].length; j++) {
            const data = segs[id][j];
            const speed_offset_key = `s${data.o.toFixed(0)}`;
            new_state[speed_offset_key] = data.v;
          }
          refMap.current.setFeatureState(
            {
              source: kSegmentSourceId,
              sourceLayer: kSegmentLayerSourceLayerId,
              id,
            },
            new_state,
          ); // Need to convert time -> offset by dividing by length of bin, then format the speed key with it
        }
      }
      return true;
    }
    return false;
  }

  function updateSlotsRepresented() {
    refUpdateSlotsRepresentedTimeout.current = null;
    if (refMap.current && mapHasLayer(refMap.current, kSegmentLayerId)) {
      // console.debug('Update slots represented');
      const renderedFeatures = refMap.current.querySourceFeatures(
        kSegmentSourceId,
        {
          sourceLayer: kSegmentLayerSourceLayerId,
        },
      );
      const slots = {};
      let relevant_features = 0;
      for (let i = 0; i < renderedFeatures.length; i++) {
        // Probably can magically subselect only a handful of features? Might only ever get to around 1000 so not worth it
        const feature = renderedFeatures[i];
        if (feature.properties.date === refTargetDate.current) {
          relevant_features += 1;
          const props = feature.properties || {};
          const state = feature.state || {};
          const keys = [...Object.keys(props), ...Object.keys(state)];
          for (let j = 0; j < keys.length; j++) {
            const k = keys[j];
            if (k.startsWith('s') && (props[k] || state[k])) {
              slots[k] = (slots[k] || 0) + 1;
            }
          }
        }
      }
      const final_slots = {};
      const slots_keys = Object.keys(slots);
      for (let i = 0; i < slots_keys.length; i++) {
        const k = slots_keys[i];
        if (slots[k] > relevant_features / 2) {
          // Slot is represented if more than 1/2 of the features have it
          final_slots[k.replace('s', '')] = true;
        }
      }
      dispatch(setRepresentedSlots(final_slots));
      return true;
    }
    return false;
  }

  const callUpdateSlotsRepresentedDelayed = () => {
    if (refUpdateSlotsRepresentedTimeout.current) {
      clearTimeout(refUpdateSlotsRepresentedTimeout.current);
      // console.log('Cleared timeout');
    }
    refUpdateSlotsRepresentedTimeout.current = setTimeout(
      updateSlotsRepresented,
      kUpdateSlotsRepresentedTimeoutMs,
    );
  };

  const handleSourceDataEvent = useCallbackWithErrorHandling(
    (e: mapboxgl.MapSourceDataEvent) => {
      // Given we now rely on the segments being loaded via tiles to render
      // Selected and routes, we have to listen to new tiles being rendered
      // In case the selected segments or any of the route segments are suddenly in view
      if (e.sourceId === kSegmentSourceId) {
        const tileId = e.tile?.tileID?.canonical;
        if (tileId) {
          const textTileId = `${refTargetDate.current},${tileId.z},${tileId.x},${tileId.y}`;
          if (
            !refEnrichedTiles.current[textTileId] &&
            !refDayInfo.current?.is_nrt
          ) {
            const vnrt_enriched = updateFeaturesWithVNRT();
            const rt_enriched =
              updateFeaturesWithRt() || refDayInfo.current?.is_complete_vnrt;
            callUpdateSlotsRepresentedDelayed();
            const enriched = rt_enriched && vnrt_enriched;
            if (enriched) {
              refEnrichedTiles.current[textTileId] = true;
              // console.log('Marking tile enriched', textTileId);
            } else {
              // console.log('Tile not sufficiently enriched', textTileId, vnrt_enriched, rt_enriched, slots_enriched);
            }
          } else {
            // console.log('Tile already enriched', textTileId);
          }
        }
      }
    },
  );

  const handleMapLoadCallback = useCallbackWithErrorHandling(() => {
    // console.log('Map ready!');
    setMapReady(true);
    refStyleReady.current = true;
    updateFeaturesWithRt();
    updateFeaturesWithVNRT();
    updateSlotsRepresented();
  });

  const handleMapSettingsClickCallback = () => {
    setSettingsOpen(true);
  };

  useEffect(() => {
    // Wipe old map
    if (refMap.current) {
      refMap.current.remove();
      refMap.current = undefined;
      setMapReady(false);
    }

    const { center, zoom } = getInitialCenter(
      userProject,
      currentMapCenter,
      mapZoom,
    );
    if (!center) {
      // Project defaults not yet loaded, skipping initialisation of map
      return;
    }

    // Init new map
    refStyleReady.current = false;
    setMapReady(false);
    initMap(
      refMap,
      refMapContainer,
      center,
      zoom,
      mapStyle,
      true,
      {
        handleMapLoadCallback,
        handleMapMoveCallback,
        handleSegmentClickCallback,
        handleMapSettingsClickCallback,
        handleRoadClassFilterClickCallback,
      },
      dispatch,
      userProject,
    );
    lastColorUpdate = nowUnix();
    refMap.current.on('mousemove', handleMouseMoveEvent);
    refMap.current.on('sourcedata', handleSourceDataEvent);
    refMap.current.addControl(
      new MapControl(
        kTicksShowHideId,
        'Show/hide directionality barbs',
        showBarbs,
        handleTicksShowHideClickCallback,
      ),
    );
    if (projectHasIncidents) {
      refMap.current.addControl(
        new MapControl(
          kIncidentsShowHideId,
          isRealtimeMetric(timelineMetric)
            ? kIncidentsShowHideNormalMessage
            : kIncidentsShowHideDisabledMessage,
          isRealtimeMetric(timelineMetric)
            ? showIncidents
            : MapControlValue.disabled,
          handleIncidentsShowHideClickCallback,
        ),
      );
    }
    (refMap.current as any).cemStyle = mapStyle;
  }, [userProject, mapStyle]); // eslint-disable-line react-hooks/exhaustive-deps

  // redrawSegments
  useEffect(
    () => {
      // console.log(`============= useeffect redrawSegments (mapReady: ${mapReady}`);
      // console.log('Map ready: ', mapReady);
      if (
        mapReady &&
        refMap.current &&
        refStyleReady.current &&
        timelineMetric &&
        comparison
      ) {
        const redrawn = redrawBaseSegments(
          mapReady,
          refMap.current,
          [
            segmentLayerDefinition,
            segmentArrowLayerDefinition,
            hoverLayerDefinition,
          ],
          `server/tiles/timeline/${project}/${targetDate}/${comparison}`,
          TIMELINE_MIN_ZOOM,
          16,
        );
        if (redrawn) {
          refEnrichedTiles.current = {};
        }
        if (projectHasIncidents) {
          redrawIncidents(
            refMap.current,
            mapStyle,
            userProject.project_slug,
            shouldShowIncidents(showIncidents, timelineMetric),
          );
        }
      }
    },
    // we don't need dependency on selectedSegmentId even though we draw the selected statement
    // because (besides initial render) it only erasing/drawing it is handled by handleSegmentClickCallback
    [
      mapReady,
      targetDate,
      comparison,
      selectedSegmentId,
      selectedRoute,
      timelineMetric,
    ], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (refMap.current) {
      const { center } = getInitialCenter(
        userProject,
        currentMapCenter,
        mapZoom,
      );
      refMap.current.jumpTo({ center });
    }
  }, [userProject]);

  useEffect(() => {
    if (mapShowingSegments(refMap.current)) {
      if (
        !lastColorUpdate ||
        nowUnix() - lastColorUpdate > kColorUpdateMillis
      ) {
        clearTimeout(lastColorUpdateRef);
        updateColorExpression();
      } else {
        clearTimeout(lastColorUpdateRef);
        lastColorUpdateRef = setTimeout(
          updateColorExpression,
          kColorUpdateMillis,
        );
      }
    }
  }, [
    timeOfDayOffset,
    timelineMetric,
    targetDate,
    legendSettings,
    roadFilters,
  ]);

  useEffect(() => {
    updateMapLocation(refMap.current, mapZoom, currentMapCenter);
  }, [currentMapCenter, mapZoom]);

  useEffect(() => {
    if (refTargetDate.current !== targetDate) {
      console.log('Changed dates, wiping represented slots and enriched tiles');
      dispatch(setRepresentedSlots({}));
      refEnrichedTiles.current = {}; // Potentially duplicative of redraw Sements
    }
    refTargetDate.current = targetDate;
  }, [targetDate]);

  useEffect(() => {
    refRealtimeData.current = realtimeData;
    updateFeaturesWithRt();
  }, [realtimeData, timelineMetric]);

  useEffect(() => {
    updateFeaturesWithVNRT();
  }, [timelineMetric, refVnrtData.current]);

  useEffect(() => {
    refTimelineMetric.current = timelineMetric;
  }, [timelineMetric]);

  useEffect(() => {
    refVnrtData.current = vnrtData;
  }, [vnrtData]);

  useEffect(() => {
    refDayInfo.current = day_info;
  }, [day_info]);

  useEffect(() => {
    refProjectUsesMetric.current = userProject?.uses_metric;
  }, [userProject]);

  useEffect(() => {
    refShowIncidents.current = showIncidents;
    if (mapReady) {
      setIncidentLayerVisibility(refMap.current, showIncidents, timelineMetric);
      if (!isRealtimeMetric(timelineMetric)) {
        MapControl.setValue(MapControlValue.disabled, kIncidentsShowHideId);
        MapControl.setTitle(
          kIncidentsShowHideDisabledMessage,
          kIncidentsShowHideId,
        );
      } else {
        MapControl.setValue(showIncidents, kIncidentsShowHideId);
        MapControl.setTitle(
          kIncidentsShowHideNormalMessage,
          kIncidentsShowHideId,
        );
      }
    }
  }, [timelineMetric, showIncidents, mapReady]);

  useEffect(() => {
    refInLocationEditMode.current = inLocationEditMode;
    if (mapReady) {
      if (inLocationEditMode) {
        if (!displayedBookmark) {
          dispatch(setDisplayedBookmark({ ...refMap.current.getCenter() }));
        } else {
          const bookmarks = {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: latLngToJsonPoint(displayedBookmark),
              },
            ],
          };
          showBookmarks(refMap.current, bookmarks, mapStyle);
        }
      } else {
        safeRemoveBookmarks(refMap.current);
      }
    }
  }, [mapReady, dispatch, inLocationEditMode, displayedBookmark]);

  return (
    <div ref={refMapContainer} className={styles.map}>
      <MapLoadingSpinner currentRefMap={refMap} mapReady={mapReady} />
      <TimelineLegend
        hourlyMaxCount={hourlyMaxCount.current}
        metricsOptions={getTLMetricOptions(mapStyle, timelineMetric)}
      />
      {zoomPrompt && <FloatingDiv>Zoom in to view timeline</FloatingDiv>}
      <OutsideBboxPrompt currentRefMap={refMap} />
      <MapSettings
        open={settingsOpen}
        handleVisSwitch={(visible) => setSettingsOpen(visible)}
      />
      <RoadClassTooltip
        open={roadFilterOpen}
        handleVisSwitch={(visible) => setRoadFilterOpen(visible)}
        anchorEl={anchorEl}
      />
      <TimelineMapSegmentTooltip data={tooltipData} />
      <ScreenshotExtras info={{ time: formatTimeOfDay(timeOfDay) }} />
    </div>
  );
}
